import { Component } from 'react';
import AriaModal from 'react-aria-modal';
import { Button, ButtonVariation } from '../Button/Button';
import { ButtonToolbar } from '../ButtonToolbar/ButtonToolbar';
import { IconButton } from '../IconButton/IconButton';
import {
  ContentElement,
  CustomFooterElement,
  FooterElement,
  HeaderButtonElement,
  HeaderContentElement,
  HeaderElement,
  ModalElement,
  ModalGlobalStyle,
  SubHeader,
  HeaderTop,
  FooterAlert,
} from './Modal.styles';
import React from 'react';
import { Tooltip } from '../Tooltip/Tooltip';

export type ModalSize = 'small' | 'medium' | 'large' | 'tall' | 'wide' | 'fullscreen';
export type ModalVariation = 'normal' | 'clean';

const MODAL_BODY_HEIGHT = 450;

export interface IProps extends IntrinsicProps {
  /** The modal heading text. Defaults to undefined */
  heading?: string;
  /** The modal subheading text. Defaults to undefined */
  subHeader?: React.ReactElement | string;
  /** The modal size. "small" | "medium" (default) | "large" " | "tall" " | "wide" */
  size?: ModalSize;
  /** "normal" (default) | "clean". */
  variation?: ModalVariation;
  /** Whether the modal is displayed or not.  Defaults to false. */
  isOpen: boolean;
  /** Event fired when close button is clicked. */
  onCloseButtonClicked?: (e?: any) => void;
  /** Event fired when cancel button is clicked. */
  onCancelButtonClicked?: (e?: any) => void;
  /** Event fired when ok button is clicked. */
  onOkButtonClicked?: (e?: any) => void;
  /** Label of the okay button.  Defaults to "Ok" */
  okButtonLabel?: string;
  /** Ok Button ARIA label tag */
  okButtonAriaLabel?: string;
  /** Tooltip displayed when the ok button is disabled. */
  okButtonDisabledTooltip?: string;
  /** Label of the cancel button.  Defaults to "Cancel" */
  cancelButtonLabel?: string;
  /** cancel Button ARIA label tag */
  cancelButtonAriaLabel?: string;
  /** Type of the ok button.  Defaults to "primary" */
  okButtonType?: ButtonVariation;
  /** Type of the cancel button.  Defaults to "normal" */
  cancelButtonType?: ButtonVariation;
  /** True to center buttons, false to right-align them.  Defaults to false. */
  centerButtons?: boolean;
  /** Slot for adding custom components (such as a checkbox for example) to the footer to the left of the buttons. */
  customFooter?: React.ReactNode;
  /** Disable ok button. Defaults to false. */
  okButtonDisabled?: boolean;
  /** Disable cancel button. Defaults to false. */
  cancelButtonDisabled?: boolean;
  /** Disable close button. Defaults to false. */
  closeButtonDisabled?: boolean;
  /** Overflow type.  Defaults to "auto" */
  overflow?: string;
  /** Minimum width of modal.  Defaults to 400px */
  minWidth?: number;
  /** Minimum height of modal.  Defaults to 300px */
  minHeight?: number;
  /** Enable or disable maxHeight on the modal container. true (default) | false */
  maxHeight?: boolean;
  /** Disable pressing escape to close the modal. Defaults to false. */
  disableEscClose?: boolean;
  /** Disable clicking modal overlay to close the modal. Defaults to false. */
  disableModalOverlayClose?: boolean;
  /** Optional header container. Appears to the right of the Heading. Defaults to undefined. */
  customHeader?: JSX.Element;
  /** Optional footer alert. Appears to the top of the footer. Defaults to undefined. */
  footerAlert?: JSX.Element;
}

export interface IState {
  isMounted: boolean;
  modalActive: boolean;
  modalEntered: boolean;
  modalBodyHeight?: any;
}

export type ModalProps = IProps;

export class Modal extends Component<IProps, IState> {
  public elementRef: React.RefObject<any>;
  public constructor(props) {
    super(props);
    this.elementRef = React.createRef();
  }

  public static defaultProps: Pick<IProps, 'overflow' | 'minWidth' | 'minHeight'> = {
    overflow: 'auto',
    minWidth: 400,
    minHeight: 200,
  };

  public state: IState = {
    isMounted: false,
    modalActive: false,
    modalEntered: false,
    modalBodyHeight: 0,
  };

  private timeout: any;

  public componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  public componentDidMount() {
    const { isOpen } = this.props;

    this.setState({ isMounted: true }, () => {
      if (isOpen) {
        this.activateModal();
      }
    });
  }

  public componentDidUpdate(prevProps) {
    const { isOpen } = this.props;
    let timeout;
    if (prevProps.isOpen !== isOpen) {
      if (isOpen) {
        this.activateModal();
      } else {
        this.deactivateModal();
        clearInterval(timeout);
      }
    }
    if (prevProps.modalBodyHeight !== this.state.modalBodyHeight) {
      timeout = setTimeout(() => {
        this.scrollElement();
      }, 100);
    }
  }

  public scrollElement() {
    if (this.elementRef.current) {
      const { height } = this.elementRef.current.getBoundingClientRect();

      if (height > MODAL_BODY_HEIGHT) {
        this.elementRef.current.scrollIntoView({ behavior: 'smooth' });
        this.elementRef.current.style.overflow = 'auto';
        this.elementRef.current.style.height = `${MODAL_BODY_HEIGHT} px`;
        this.setState({ modalBodyHeight: height });
      }
    }
  }

  public render() {
    const { isMounted, modalActive, modalEntered } = this.state;

    const {
      heading,
      size,
      variation,
      onCloseButtonClicked,
      onCancelButtonClicked,
      onOkButtonClicked,
      okButtonLabel,
      okButtonAriaLabel,
      cancelButtonLabel,
      cancelButtonAriaLabel,
      okButtonType,
      cancelButtonType,
      centerButtons,
      customFooter,
      okButtonDisabled,
      cancelButtonDisabled,
      closeButtonDisabled,
      overflow,
      minWidth,
      minHeight,
      maxHeight,
      disableEscClose,
      disableModalOverlayClose,
      customHeader,
      subHeader,
      footerAlert,
      okButtonDisabledTooltip,
      ...props
    } = this.props;

    if (!isMounted) {
      return null;
    }

    const underlayClickExits = !disableModalOverlayClose && !closeButtonDisabled && !!onCloseButtonClicked;

    const escapeExits = !disableEscClose && !closeButtonDisabled && !!onCloseButtonClicked;

    let underlayClass = 'underlay';
    if (modalEntered) {
      underlayClass += ' underlay--open';

      if (!underlayClickExits) {
        underlayClass += ' underlay--no-click';
      }
    }

    return (
      <>
        <ModalGlobalStyle />

        <AriaModal
          escapeExits={escapeExits}
          focusDialog={true}
          getApplicationNode={this.getApplicationNode}
          mounted={modalActive}
          onExit={onCloseButtonClicked}
          titleText={heading}
          underlayClass={underlayClass}
          underlayClickExits={underlayClickExits}
          underlayColor={null}
          verticallyCenter={true}
        >
          <ModalElement
            data-testid="Modal"
            isOpen={modalEntered}
            size={size}
            overflow={overflow}
            minWidth={minWidth}
            minHeight={minHeight}
            maxHeight={maxHeight}
            {...props}
          >
            <HeaderElement variation={variation} subHeader={!!subHeader}>
              <HeaderTop>
                <HeaderContentElement>{heading}</HeaderContentElement>
                {onCloseButtonClicked && (
                  <HeaderButtonElement>
                    <IconButton
                      onClick={onCloseButtonClicked}
                      disabled={closeButtonDisabled}
                      ariaLabel="close"
                      icon="times"
                    />
                  </HeaderButtonElement>
                )}
                {customHeader ? customHeader : null}
              </HeaderTop>
              {subHeader ? <SubHeader data-testid="sub-header"> {subHeader}</SubHeader> : null}
            </HeaderElement>

            <ContentElement ref={this.elementRef} overflow={overflow}>
              {this.props.children}
            </ContentElement>
            {(customFooter || onCancelButtonClicked || onOkButtonClicked) && (
              <FooterElement
                variation={variation}
                centerButtons={centerButtons}
                overflowVisible={overflow === 'visible'}
                footerAlert={footerAlert}
              >
                {footerAlert && <FooterAlert>{footerAlert}</FooterAlert>}
                <ButtonToolbar>
                  {onCancelButtonClicked && (
                    <Button
                      ariaLabel={cancelButtonAriaLabel}
                      variation={cancelButtonType || 'secondary'}
                      onClick={this.handleCancelButtonClicked}
                      disabled={cancelButtonDisabled}
                    >
                      {cancelButtonLabel || 'Cancel'}
                    </Button>
                  )}
                  {onOkButtonClicked && (
                    <OkButton
                      okButtonAriaLabel={okButtonAriaLabel}
                      okButtonType={okButtonType}
                      handleOkButtonClicked={this.handleOkButtonClicked}
                      okButtonDisabled={okButtonDisabled}
                      okButtonLabel={okButtonLabel}
                      okButtonDisabledTooltip={okButtonDisabledTooltip}
                    />
                  )}
                </ButtonToolbar>
                {customFooter && <CustomFooterElement>{customFooter}</CustomFooterElement>}
              </FooterElement>
            )}
          </ModalElement>
        </AriaModal>
      </>
    );
  }

  private activateModal = () => {
    this.setState(
      {
        modalActive: true,
      },
      () => {
        clearTimeout(this.timeout);
        this.timeout = window.setTimeout(() => {
          this.setState({
            modalEntered: true,
          });
        }, 200);
      },
    );
  };

  private deactivateModal = () => {
    this.setState(
      {
        modalEntered: false,
      },
      () => {
        clearTimeout(this.timeout);
        this.timeout = window.setTimeout(() => {
          this.setState({
            modalActive: false,
          });
        }, 200);
      },
    );
  };

  private getApplicationNode = () => {
    return document.getElementById('app-root');
  };

  private handleOkButtonClicked = e => {
    e.stopPropagation();
    if (this.props.onOkButtonClicked) {
      this.props.onOkButtonClicked(e);
    }
  };

  private handleCancelButtonClicked = e => {
    e.stopPropagation();
    if (this.props.onCancelButtonClicked) {
      this.props.onCancelButtonClicked(e);
    }
  };
}

const OkButton = ({
  okButtonAriaLabel,
  okButtonType,
  handleOkButtonClicked,
  okButtonDisabled,
  okButtonLabel,
  okButtonDisabledTooltip,
}) => {
  if (okButtonDisabledTooltip) {
    return (
      <Tooltip title={okButtonDisabledTooltip}>
        <Button
          ariaLabel={okButtonAriaLabel}
          variation={okButtonType || 'primary'}
          onClick={handleOkButtonClicked}
          disabled={okButtonDisabled}
        >
          {okButtonLabel || 'Ok'}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button
      ariaLabel={okButtonAriaLabel}
      variation={okButtonType || 'primary'}
      onClick={handleOkButtonClicked}
      disabled={okButtonDisabled}
    >
      {okButtonLabel || 'Ok'}
    </Button>
  );
};
